import { useEffect, useState } from 'react';
import { CaretDown, ChatCircleDots, CheckCircle, ArrowLineUp, At } from '@phosphor-icons/react';
import { getAsset, compareAmounts, type AssetCode, type ChequeDTO, type ContactDTO } from '@onyx/shared';
import { useWallet, useContacts, useMoneyMutation } from '../lib/queries';
import { availableOf, useFiat } from '../lib/app';
import { useLang, useT, errorText } from '../lib/i18n';
import { displayDigits, formatCrypto, formatFiat } from '../lib/format';
import { navigate, switchTab } from '../lib/router';
import { api, ApiError } from '../lib/api';
import { haptic, shareInline } from '../lib/telegram';
import { AssetChip } from '../components/AssetChip';
import { AssetList } from '../components/Pickers';
import { AmountDisplay, Keypad } from '../components/Keypad';
import { Avatar, Field, Row, Screen, ScreenHeader, Section, Sheet, Slab, toast } from '../components/ui';

/** Asset switcher shared by keypad screens. */
export function AssetSwitch({ asset, onPick, onlyHeld = true }: { asset: AssetCode; onPick: (a: AssetCode) => void; onlyHeld?: boolean }) {
  const t = useT();
  const lang = useLang();
  const wallet = useWallet();
  const [open, setOpen] = useState(false);
  const bal = availableOf(wallet.data, asset);
  return (
    <>
      <button type="button" className="asset-switch" onClick={() => setOpen(true)} aria-haspopup="dialog">
        <AssetChip asset={asset} size={32} />
        <span className="asset-switch__text">
          <span className="asset-switch__code">{asset}</span>
          <span className="asset-switch__bal num">
            {t['common.balance']}: {formatCrypto(bal, displayDigits(Number(bal), getAsset(asset)!.stable), lang)}
          </span>
        </span>
        <CaretDown size={16} weight="bold" aria-hidden="true" />
      </button>
      <Sheet open={open} onClose={() => setOpen(false)} title={t['pick.asset']}>
        <AssetList
          wallet={wallet.data}
          filter={onlyHeld ? (c) => Number(availableOf(wallet.data, c)) > 0 : undefined}
          onPick={(c) => {
            haptic.select();
            onPick(c);
            setOpen(false);
          }}
        />
      </Sheet>
    </>
  );
}

export function Send({ initial }: { initial?: AssetCode }) {
  const t = useT();
  const lang = useLang();
  const fiat = useFiat();
  const wallet = useWallet();
  const firstHeld = wallet.data?.balances.find((b) => Number(b.available) > 0)?.asset;
  const [asset, setAsset] = useState<AssetCode>(initial ?? firstHeld ?? 'USDT');
  const [amount, setAmount] = useState('');
  const [step, setStep] = useState<'amount' | 'to' | 'done'>('amount');
  const [to, setTo] = useState('');
  const [comment, setComment] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [selected, setSelected] = useState<ContactDTO | null>(null);
  const [debouncedTo, setDebouncedTo] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setDebouncedTo(to), 250);
    return () => clearTimeout(id);
  }, [to]);
  const contactsQ = useContacts(debouncedTo.trim());
  const def = getAsset(asset)!;
  const available = availableOf(wallet.data, asset);
  const price = wallet.data?.prices[asset]?.[fiat] ?? 0;
  const over = !!amount && compareAmounts(amount, available) > 0;
  const valid = !!amount && Number(amount) > 0 && !over;

  const transfer = useMoneyMutation((v: { to: string; asset: string; amount: string; comment?: string }) =>
    api<{ id: string; to: { firstName: string; username: string | null } }>('/transfer', { body: v }),
  );
  const cheque = useMoneyMutation((v: { asset: string; amountPer: string; activations: number }) => api<ChequeDTO>('/cheques', { body: v }));

  const fail = (e: unknown) => {
    haptic.error();
    toast.bad(errorText(t, e instanceof ApiError ? e.code : undefined));
  };

  if (step === 'done') {
    return (
      <Screen className="done" footer={<Slab onClick={() => switchTab('/')}>{t['common.done']}</Slab>}>
        <div className="done__body">
          <span className="done__mark">
            <CheckCircle size={56} weight="fill" aria-hidden="true" />
          </span>
          <h1>{t['send.doneTitle']}</h1>
          <p className="num">
            {formatCrypto(amount, def.decimals, lang)} {asset} → {sentTo}
          </p>
          <p className="done__note">{t['send.free']}</p>
        </div>
      </Screen>
    );
  }

  if (step === 'to') {
    const query = debouncedTo.trim().replace(/^@/, '');
    const contacts = contactsQ.data?.items ?? [];
    const showList = !selected && (query === '' ? contacts.length > 0 : query.length >= 3);
    return (
      <Screen
        className="send-to"
        footer={
          <Slab
            disabled={!selected}
            loading={transfer.isPending}
            onClick={() =>
              selected &&
              transfer.mutate(
                { to: selected.handle, asset, amount, comment: comment.trim() || undefined },
                {
                  onSuccess: (r) => {
                    haptic.success();
                    setSentTo(r.to.username ? `@${r.to.username}` : r.to.firstName);
                    setStep('done');
                  },
                  onError: fail,
                },
              )
            }
          >
            {t['send.submit']} {formatCrypto(amount, def.decimals, lang)} {asset}
            {selected ? ` ${selected.username ? `@${selected.username}` : selected.firstName}` : ''}
          </Slab>
        }
      >
        <ScreenHeader title={t['send.chooseHow']} onBack={() => setStep('amount')} sub={<span className="num">{formatCrypto(amount, def.decimals, lang)} {asset}</span>} />

        <button
          type="button"
          className="option"
          disabled={cheque.isPending}
          onClick={() =>
            cheque.mutate(
              { asset, amountPer: amount, activations: 1 },
              {
                onSuccess: (c) => {
                  haptic.success();
                  shareInline(c.id, c.link);
                  navigate(`/pay/cheque/${c.id}`, { replace: true });
                },
                onError: fail,
              },
            )
          }
        >
          <span className="option__icon">
            <ChatCircleDots size={24} weight="fill" aria-hidden="true" />
          </span>
          <span className="option__text">
            <span className="option__title">{t['send.asCheque']}</span>
            <span className="option__sub">{t['send.chequeHint']}</span>
          </span>
        </button>

        <div className="divider" role="separator">
          <span>{t['send.byUsername']}</span>
        </div>

        {selected ? (
          <div className="recipient">
            <Avatar user={selected} size={44} />
            <span className="recipient__text">
              <span className="recipient__name">{selected.firstName}</span>
              <span className="recipient__sub">
                {selected.username ? `@${selected.username} · ` : ''}
                {t['send.free']}
              </span>
            </span>
            <button type="button" className="link-btn" onClick={() => setSelected(null)}>
              {t['send.change']}
            </button>
          </div>
        ) : (
          <Field
            label={t['send.to']}
            htmlFor="send-to"
            hint={query.length >= 3 && contactsQ.isSuccess && contacts.length === 0 ? t['send.notFound'] : null}
          >
            <div className="input-group">
              <At size={18} className="input-group__lead" aria-hidden="true" />
              <input
                id="send-to"
                className="input"
                value={to}
                placeholder={t['send.toPh']}
                autoCapitalize="off"
                autoComplete="off"
                spellCheck={false}
                onChange={(e) => setTo(e.target.value.replace(/[\s@]/g, ''))}
              />
            </div>
          </Field>
        )}
        {showList && contacts.length > 0 && (
          <Section title={query ? t['send.results'] : t['send.recent']}>
            <div className="rows">
              {contacts.map((c) => (
                <Row
                  key={c.handle}
                  onClick={() => {
                    haptic.select();
                    setSelected(c);
                  }}
                  leading={<Avatar user={c} size={40} />}
                  title={c.firstName}
                  subtitle={c.username ? `@${c.username}` : c.handle}
                  chevron
                />
              ))}
            </div>
          </Section>
        )}
        <Field label={t['send.comment']} htmlFor="send-comment">
          <input id="send-comment" className="input" value={comment} maxLength={140} placeholder={t['send.commentPh']} onChange={(e) => setComment(e.target.value)} />
        </Field>

        <button type="button" className="option option--quiet" onClick={() => navigate(`/withdraw/${asset}`)}>
          <span className="option__icon">
            <ArrowLineUp size={22} aria-hidden="true" />
          </span>
          <span className="option__text">
            <span className="option__title">{t['send.toAddress']}</span>
          </span>
        </button>
      </Screen>
    );
  }

  return (
    <Screen
      className="keypad-screen"
      footer={
        <>
          <Keypad onChange={setAmount} decimals={def.decimals} />
          <Slab disabled={!valid} onClick={() => setStep('to')}>
            {t['common.continue']}
          </Slab>
        </>
      }
    >
      <ScreenHeader title={t['send.title']} />
      <div className="keypad-screen__top">
        <AssetSwitch
          asset={asset}
          onPick={(a) => {
            setAsset(a);
            setAmount('');
          }}
        />
        <button type="button" className="tool-btn tool-btn--solid" onClick={() => setAmount(available === '0' ? '' : available)}>
          {t['withdraw.max']}
        </button>
      </div>
      <AmountDisplay
        value={amount}
        unit={asset}
        invalid={over}
        sub={over ? <span className="is-bad">{t['err.insufficient_funds']}</span> : <span className="num">≈ {formatFiat(Number(amount || 0) * price, fiat, lang)}</span>}
      />
    </Screen>
  );
}
